// pages/VendorLogin.jsx
// Vendor Login Page with Firebase Authentication
// ============================================
// Updated Dec 31, 2025 - Firebase auth (4.12)
// Supports Email/Password login for vendors
// Includes password reset functionality
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Store, 
  Mail, 
  Lock, 
  ArrowLeft, 
  AlertCircle, 
  Users, 
  Eye, 
  EyeOff,
  CheckCircle 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

const VendorLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotification();
  const { 
    signInWithEmail, 
    resetPassword, 
    isLoggedIn, 
    user,
    loading: authLoading, 
    error: authError, 
    clearError 
  } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Password reset state
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  
  // Get redirect destination
  const from = location.state?.from?.pathname || '/vendor/dashboard';
  
  // Redirect if already logged in as vendor
  useEffect(() => {
    if (isLoggedIn && user) {
      // Check if user is a vendor
      const isVendor = user.userTypes?.includes('vendor') || 
                       user.isVendor || 
                       user.vendorStatus === 'approved';
      
      if (isVendor) {
        navigate(from, { replace: true });
      }
    }
  }, [isLoggedIn, user, navigate, from]);
  
  // Clear errors on mount
  useEffect(() => {
    clearError?.();
    return () => clearError?.();
  }, [clearError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (error) setError('');
  };

  // Handle email/password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validate
    if (!formData.email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!formData.password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);

    try {
      const result = await signInWithEmail(formData.email, formData.password);
      
      if (result.success) {
        // Check if user is a vendor
        const userData = result.user;
        const isVendor = userData?.userTypes?.includes('vendor') || 
                         userData?.isVendor || 
                         userData?.vendorStatus === 'approved';
        
        if (!isVendor) {
          setError('This account is not registered as a vendor. Please use the member login or register as a vendor.');
          // Sign out since they're not a vendor
          return;
        }
        
        // Check vendor approval status
        if (userData?.vendorStatus === 'pending') {
          setError('Your vendor account is pending admin approval. You will receive an email once approved.');
          return;
        }
        
        if (userData?.vendorStatus === 'rejected') {
          setError('Your vendor application was not approved. Please contact support.');
          return;
        }
        
        // Store vendor-specific data
        localStorage.setItem('userType', 'vendor');
        localStorage.setItem('currentVendor', JSON.stringify(userData));
        
        // Show success notification
        addNotification({
          type: 'success',
          title: 'Welcome back!',
          message: `Logged in as ${userData.businessName || userData.name}`,
          showToast: true
        });
        
        // Navigate to vendor dashboard
        navigate(from, { replace: true });
      } else {
        // Handle specific error messages
        let errorMessage = result.error || 'Login failed. Please try again.';
        
        if (errorMessage.includes('user-not-found') || errorMessage.includes('wrong-password')) {
          errorMessage = 'Invalid email or password';
        } else if (errorMessage.includes('too-many-requests')) {
          errorMessage = 'Too many failed attempts. Please try again later.';
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!resetEmail.trim()) {
      setError('Please enter your email');
      return;
    }
    
    setResetLoading(true);
    
    try {
      const result = await resetPassword(resetEmail);
      
      if (result.success) {
        setResetSent(true);
        setSuccessMessage(result.message || 'Password reset email sent!');
      } else {
        setError(result.error || 'Failed to send reset email');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setResetLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  // Password Reset Form
  if (showResetForm) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
        {/* Portal Switcher */}
        <div className="fixed top-4 right-4 z-50">
          <Link
            to="/login"
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-lg transition-all"
          >
            <Users size={18} />
            <span className="text-sm font-medium">Switch to Member Portal</span>
          </Link>
        </div>

        {/* Header */}
        <div className="p-4">
          <button
            onClick={() => {
              setShowResetForm(false);
              setResetSent(false);
              setError('');
              setSuccessMessage('');
            }}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to login
          </button>
        </div>

        <div className="flex flex-col items-center justify-center px-4 py-8">
          {/* Logo */}
          <div className="bg-blue-600 rounded-full p-4 mb-6">
            <Store size={40} className="text-white" />
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
          <p className="text-gray-600 mb-8">Enter your email to receive a reset link</p>

          <div className="w-full max-w-md">
            <div className="bg-white rounded-lg shadow-lg p-6">
              {resetSent ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Check your email</h3>
                  <p className="text-gray-600 mb-6">
                    We've sent a password reset link to <strong>{resetEmail}</strong>
                  </p>
                  <button
                    onClick={() => {
                      setShowResetForm(false);
                      setResetSent(false);
                    }}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Return to login
                  </button>
                </div>
              ) : (
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                      <AlertCircle size={20} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="vendor@business.com"
                        required
                        disabled={resetLoading}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      resetLoading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Login Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Portal Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <Link
          to="/login"
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-lg transition-all"
        >
          <Users size={18} />
          <span className="text-sm font-medium">Switch to Member Portal</span>
        </Link>
      </div>

      {/* Header */}
      <div className="p-4">
        <button
          onClick={handleBackToHome}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Home
        </button>
      </div>

      <div className="flex flex-col items-center justify-center px-4 py-8">
        {/* Logo */}
        <div className="bg-blue-600 rounded-full p-4 mb-6">
          <Store size={40} className="text-white" />
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Vendor Portal</h1>
        <p className="text-gray-600 mb-8">Sign in to manage your business</p>

        {/* Login Form */}
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Message */}
              {(error || authError) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                  <AlertCircle size={20} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error || authError}</p>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start">
                  <CheckCircle size={20} className="text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-800">{successMessage}</p>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="vendor@business.com"
                    required
                    autoComplete="email"
                    disabled={loading || authLoading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    disabled={loading || authLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setShowResetForm(true);
                    setResetEmail(formData.email);
                    setError('');
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || authLoading}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  loading || authLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading || authLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In to Vendor Portal'
                )}
              </button>
            </form>

            {/* Links */}
            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-gray-600">
                Not a vendor yet?{' '}
                <Link to="/vendor/register" className="text-blue-600 hover:text-blue-700 font-medium">
                  Register here
                </Link>
              </p>
              <p className="text-sm text-gray-600">
                Are you a member?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Member login
                </Link>
              </p>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              🔒 Secure vendor portal · Your data is encrypted and protected
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorLogin;